import type { OrderStatus } from '@/contexts/OrderContext';

const PAYMENT_STATUS_TOOLTIPS: Partial<Record<OrderStatus, string>> = {
  AGUARDANDO: 'Pagamento pendente',
  PAGO: 'Pedido pago',
  PARCIAL: 'Pagamento parcial',
  CANCELADO: 'Pedido cancelado',
  OUTRO: 'Status financeiro indefinido',
  LIBERADO: 'Pedido liberado',
  'ARTE FINAL': 'Etapa: Arte final',
  SEPARAÇÃO: 'Etapa: Separação',
  IMPRESSÃO: 'Etapa: Impressão',
  PRENSAGEM: 'Etapa: Prensagem',
  'CONTROLE DE QUALIDADE': 'Etapa: Controle de qualidade',
  EXPEDIÇÃO: 'Etapa: Expedição',
  'DISPONIVEL PARA COLETA': 'Disponível para coleta',
};

const DELIVERY_MIN_TOOLTIPS: Record<string, string> = {
  URGENTE: 'Entrega urgente',
  HOJE: 'Entrega hoje',
  AMANHÃ: 'Entrega amanhã',
};

export function buildItemIdentityTooltip(
  orcId: number | string | undefined,
  oitId: number | string | undefined
): string {
  if (orcId != null && oitId != null) {
    return `Pedido O-${orcId} · I-${oitId}`;
  }
  if (oitId != null) return `Item I-${oitId}`;
  return 'Item do pedido';
}

export function buildClientTooltip(name: string): string {
  return name.trim() || 'Cliente não informado';
}

export function buildProductTooltip(name?: string | null): string | undefined {
  const trimmed = name?.trim();
  return trimmed || undefined;
}

export function buildConsolidationTooltip(visibleCount: number, totalCount: number): string {
  const hidden = Math.max(0, totalCount - visibleCount);
  if (hidden <= 0) return `${totalCount} ${totalCount === 1 ? 'item' : 'itens'} no pedido`;
  return `+${hidden} ${hidden === 1 ? 'item oculto' : 'itens ocultos'} na lista`;
}

export function buildGroupItemsCountTooltip(count: number): string {
  return `${count} ${count === 1 ? 'item neste pedido' : 'itens neste pedido'}`;
}

export function buildDeliveryTooltip(
  opcao?: string | null,
  horarioCorte?: string | null,
  createdAt?: string | null
): string {
  const normalized = String(opcao || '').trim().toUpperCase();
  const base = DELIVERY_MIN_TOOLTIPS[normalized] || (normalized ? `Entrega: ${normalized}` : 'Prazo de entrega');

  const parts: string[] = [base];
  if (horarioCorte) parts.push(`corte ${horarioCorte}`);

  if (createdAt) {
    try {
      const fmt = new Date(createdAt).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
      parts.push(`criado ${fmt}`);
    } catch {
      // ignora data inválida
    }
  }

  return parts.length > 1 ? parts.join(' · ') : base;
}

export function buildStageTooltip(label: string): string {
  const trimmed = label.trim();
  if (!trimmed || trimmed.toLowerCase() === 'etapa não informada') {
    return 'Etapa não definida';
  }
  return `Etapa: ${trimmed}`;
}

export function buildPaymentStatusTooltip(status: OrderStatus): string {
  return PAYMENT_STATUS_TOOLTIPS[status] || `Status: ${status}`;
}

export function buildOrderHeaderChipTooltip(
  kind: 'pagamento' | 'status' | 'data',
  value: string
): string {
  const trimmed = value.trim();
  switch (kind) {
    case 'pagamento':
      return `Pagamento: ${trimmed}`;
    case 'status':
      return `Status: ${trimmed}`;
    case 'data':
      return `Pedido em ${trimmed}`;
    default:
      return trimmed;
  }
}

export type ProductionActionKind = 'produzir' | 'open_only' | 'waiting_file' | 'blocked';

export function buildProductionActionTooltip(kind: ProductionActionKind): string {
  switch (kind) {
    case 'produzir':
      return 'Abrir no Corel para produção';
    case 'open_only':
      return 'Abrir arquivo no Corel';
    case 'waiting_file':
      return 'Arquivo ainda não salvo';
    case 'blocked':
      return 'Arquivo indisponível';
    default:
      return 'Abrir item';
  }
}

export function buildSalesActionTooltip(
  kind: 'select' | 'resave' | 'processed' | 'paid_locked' | 'blocked'
): string {
  switch (kind) {
    case 'select':
      return 'Abrir item no Corel';
    case 'resave':
      return 'Re-salvar: pedido não pago';
    case 'processed':
      return 'Arquivo já salvo';
    case 'paid_locked':
      return 'Pedido pago — sem re-salvar';
    case 'blocked':
      return 'Item indisponível';
    default:
      return 'Abrir item';
  }
}

export function buildDetailsButtonTooltip(): string {
  return 'Ver detalhes do pedido';
}

export function buildGroupHeaderTooltip(orcId: number | string, clientName: string): string {
  return `Pedido O-${orcId} · ${clientName}`;
}

export function buildResaveTagTooltip(): string {
  return 'Re-salvar: pedido não pago';
}

export function buildProcessedTagTooltip(): string {
  return 'Arquivo já salvo';
}

export function buildPersonalizationTypeTooltip(tipo: string): string {
  return `Tipo: ${tipo.trim()}`;
}

export function buildPrimaryActionTooltip(params: {
  isProduction: boolean;
  canBeSelected: boolean;
  sysSave: boolean | null;
  isOpenOnlyFlow: boolean;
  isResave: boolean;
}): string {
  const { isProduction, canBeSelected, sysSave, isOpenOnlyFlow, isResave } = params;

  if (isProduction) {
    if (sysSave === false || !canBeSelected) {
      return buildProductionActionTooltip('waiting_file');
    }
    if (isOpenOnlyFlow) {
      return buildProductionActionTooltip('open_only');
    }
    return buildProductionActionTooltip('produzir');
  }

  if (!canBeSelected) {
    return buildSalesActionTooltip('processed');
  }
  if (isResave) {
    return buildSalesActionTooltip('resave');
  }
  return buildSalesActionTooltip('select');
}
